import type { FilterRuleModel, ValueFilterRule } from '@/shared/generated'

import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns'
import { all, intersection, isEmpty, isNil, prop, trim } from 'lodash/fp'

import { Operators } from '@/shared/components/query-builder'

type FilterValue =
	undefined | string | number | boolean | (string | number)[] | null
export const getRecentlyUsedFilter = () => {
	try {
		return JSON.parse(localStorage.getItem('recently_filter') || '{}')
	} catch (error) {
		return null
	}
}
export const saveRecentlyUsedFilter = (search: string) =>
	localStorage.setItem(
		'recently_filter',
		JSON.stringify({
			date: new Date().getTime(),
			search,
		})
	)

export const asArray = (val: FilterValue) =>
	!Array.isArray(val) ? [String(val)] : val

export const encodeRuleValue = (value: FilterValue): FilterValue => {
	if (Array.isArray(value)) {
		return value.map(encodeRuleValue) as FilterValue
	} else if (typeof value === 'string') {
		return encodeURIComponent(value)
	} else {
		return value
	}
}

/**
 * Normalize value by filter operation (for example, for IN filter, value should be array)
 * @param operation - filter operation
 * @param value - filter value
 * @returns  normalized value
 */
export const normilizeValue = (
	operation: string,
	value: FilterValue
): FilterValue => {
	switch (operation) {
		case Operators.ALL_OF:
		case Operators.ANY_OF:
		case Operators.EXACT:
		case Operators.IN:
		case Operators.NOT_IN:
		case Operators.BETWEEN:
		case Operators.DATE_BETWEEN:
		case Operators.NONE_OF:
			return Array.isArray(value)
				? value
				: value === ''
					? [value]
					: (value as string)?.split?.(',') || []
		default:
			return value
	}
}
export const filterComparator = <T>(rule: FilterRuleModel, item: T) =>
	compareByOperation(
		prop(rule.name, item),
		rule.operation as Operators,

		(rule as any).value as FilterValue
	)
/**
 * Get filtered data by rules array and data array
 * @example
 * filterByRules(
 * 	[{ name: 'id', operation: 'eq', value: 1 }],
 * 	[{ id: 1, name: 'test' }]
 * ) => [{ id: 1, name: 'test' }]
 * @param rules - array of filter rules
 * @param data  - array of data to filter
 * @returns  filtered data
 */
export const filterByRules = <T = object>(
	rules: FilterRuleModel[],
	data: T[]
) =>
	data?.filter((item) => all((rule) => filterComparator(rule, item), rules)) ||
	[]

export const compareByOperation = (
	fieldValue: FilterValue,
	operator: string,
	queryValue: FilterValue
): boolean => {
	const filter = filters[operator as keyof typeof filters] as (
		a: FilterValue,
		b: FilterValue
	) => boolean

	return filter?.(fieldValue, queryValue) ?? true
}

export const filters = {
	// Numeric
	[Operators.GT]: (a: number, b: number) => a > b,
	[Operators.LT]: (a: number, b: number) => a < b,
	[Operators.GTE]: (a: number, b: number) => a >= b,
	[Operators.LTE]: (a: number, b: number) => a <= b,
	[Operators.EQ]: (a: number, b: number) => a == b,
	[Operators.NE]: (a: number, b: number) => a != b,
	[Operators.BETWEEN]: (a: number, b: number) => {
		const range = b ? (asArray(b) as number[]) : []

		return a >= range[0] && a <= range[1]
	},

	// Boolean
	[Operators.BL]: (a: number, b: number) => Boolean(a) === Boolean(+b),

	// String
	[Operators.IN]: (a: string, b: string[] | string) =>
		Array.isArray(b) ? b.includes(String(a)) : [b].includes(String(a)),
	[Operators.NOT_IN]: (a: string, b: string[] | string) =>
		Array.isArray(b) ? !b.includes(String(a)) : ![b].includes(String(a)),
	[Operators.IS]: (a: string, b: string) =>
		isNil(a) ? a == b : a.toLowerCase() === b.toLowerCase(),
	[Operators.IS_NOT]: (a: string, b: string) =>
		a.toLowerCase() !== b.toLowerCase(),
	[Operators.CONTAINS]: (a: string, b: string) => {
		if (Array.isArray(b))
			return Boolean(a && intersection(asArray(a), asArray(b.map(trim))).length)

		return a.toLowerCase().includes(b.toLowerCase())
	},
	[Operators.NOT_CONTAINS]: (a: string, b: string) =>
		!a.toLowerCase().includes(b.toLowerCase()),

	// List
	[Operators.ANY_OF]: (a: FilterValue, b: FilterValue) =>
		Boolean(intersection(asArray(a), asArray(b)).length),

	// Any
	[Operators.IS_EMPTY]: (a: FilterValue) =>
		Array.isArray(a) ? isEmpty(a) : isNil(a) || !(a + '').length,

	[Operators.IS_NOT_EMPTY]: (a: FilterValue) =>
		Array.isArray(a) ? !isEmpty(a) : !isNil(a),

	// Date
	[Operators.DATE_BETWEEN]: (a: FilterValue, b: FilterValue) => {
		/**
		 * Element value
		 */
		const date = new Date(a as string)

		/**
		 * Unwrap query values as array of seconds
		 */
		const range = b ? asArray(b) : []

		/**
		 * Query value from date
		 */
		const from = startOfDay(new Date(range[0]))

		/**
		 * Query value to date
		 */
		const to = endOfDay(new Date(range[1]))

		/**
		 * Compare values including boundaries
		 */
		return (
			(isBefore(date, to) || isSameDay(date, to)) &&
			(isAfter(date, from) || isSameDay(date, from))
		)
	},
}

export const getFilterValueByName = (
	filters: ValueFilterRule[],
	name: string
) => {
	const filter = filters.find((f: FilterRuleModel) => f.name === name)
	if (filter) {
		return normilizeValue(filter.operation, filter.value as any)
	}

	return undefined
}
